import { MakeLogicType, actions, kea, listeners, path, reducers, selectors } from 'kea'

import api from 'lib/api'
import { teamLogic } from 'scenes/teamLogic'

const tagsRequests = new Map<number, Promise<string[]>>()
const latestTagsRequests = new Map<number, Promise<string[]>>()

function fetchProjectTags(projectId: number, force = false): Promise<string[]> {
    const existingRequest = tagsRequests.get(projectId)
    if (!force && existingRequest) {
        return existingRequest
    }

    const request = api.tags.list(projectId).then((tags) => tags || [])
    tagsRequests.set(projectId, request)
    latestTagsRequests.set(projectId, request)

    const removeRequest = (): void => {
        if (tagsRequests.get(projectId) === request) {
            tagsRequests.delete(projectId)
        }
    }
    void request.then(removeRequest, removeRequest)

    return request
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface tagsModelValues {
    activeProjectId: number | null
    tags: string[]
    tagsByProject: Record<number, string[]>
    tagsLoading: boolean
    tagsLoadingByProject: Record<number, boolean>
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface tagsModelActions {
    loadTags: () => {
        value: true
    }
    loadTagsIfNeeded: () => {
        value: true
    }
    loadTagsSuccess: (
        tags: string[],
        projectId?: number
    ) => {
        projectId: number | undefined
        tags: string[]
    }
    refreshTags: () => {
        value: true
    }
    setActiveProjectId: (projectId: number | null) => {
        projectId: number | null
    }
    setProjectTags: (
        projectId: number,
        tags: string[]
    ) => {
        projectId: number
        tags: string[]
    }
    setProjectTagsLoading: (
        projectId: number,
        loading: boolean
    ) => {
        loading: boolean
        projectId: number
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface tagsModelMeta {
    __keaTypeGenInternalSelectorTypes: {
        tags: (activeProjectId: number | null, tagsByProject: Record<number, string[]>) => string[]
        tagsLoading: (activeProjectId: number | null, tagsLoadingByProject: Record<number, boolean>) => boolean
    }
}

export type tagsModelType = MakeLogicType<tagsModelValues, tagsModelActions, Record<string, any>, tagsModelMeta>

export const tagsModel = kea<tagsModelType>([
    path(['models', 'tagsModel']),
    actions({
        loadTags: true,
        loadTagsIfNeeded: true,
        loadTagsSuccess: (tags: string[], projectId?: number) => ({ tags, projectId }),
        refreshTags: true,
        setActiveProjectId: (projectId: number | null) => ({ projectId }),
        setProjectTags: (projectId: number, tags: string[]) => ({ projectId, tags }),
        setProjectTagsLoading: (projectId: number, loading: boolean) => ({ projectId, loading }),
    }),
    reducers({
        activeProjectId: [
            null as number | null,
            {
                setActiveProjectId: (_, { projectId }) => projectId,
            },
        ],
        tagsByProject: [
            {} as Record<number, string[]>,
            {
                setProjectTags: (state, { projectId, tags }) => ({ ...state, [projectId]: tags }),
            },
        ],
        tagsLoadingByProject: [
            {} as Record<number, boolean>,
            {
                setProjectTagsLoading: (state, { projectId, loading }) => ({ ...state, [projectId]: loading }),
            },
        ],
    }),
    selectors({
        tags: [
            (s) => [s.activeProjectId, s.tagsByProject],
            (projectId: number | null, tagsByProject: Record<number, string[]>): string[] => {
                return projectId === null ? [] : (tagsByProject[projectId] ?? [])
            },
        ],
        tagsLoading: [
            (s) => [s.activeProjectId, s.tagsLoadingByProject],
            (projectId: number | null, tagsLoadingByProject: Record<number, boolean>): boolean => {
                return projectId === null ? false : (tagsLoadingByProject[projectId] ?? false)
            },
        ],
    }),
    listeners(({ actions, values }) => {
        const loadProjectTags = async (force: boolean): Promise<void> => {
            const projectId = teamLogic.values.currentTeamId
            if (projectId === null) {
                return
            }

            actions.setActiveProjectId(projectId)
            actions.setProjectTagsLoading(projectId, true)
            const request = fetchProjectTags(projectId, force)
            try {
                const tags = await request
                if (latestTagsRequests.get(projectId) === request) {
                    actions.loadTagsSuccess(tags, projectId)
                }
            } finally {
                if (latestTagsRequests.get(projectId) === request) {
                    actions.setProjectTagsLoading(projectId, false)
                }
            }
        }

        return {
            loadTags: () => loadProjectTags(true),
            loadTagsIfNeeded: () => {
                const projectId = teamLogic.values.currentTeamId
                if (projectId === null) {
                    return
                }

                actions.setActiveProjectId(projectId)
                if (values.tagsByProject[projectId]) {
                    return
                }

                return loadProjectTags(false)
            },
            loadTagsSuccess: ({ tags, projectId }) => {
                const resolvedProjectId = projectId ?? teamLogic.values.currentTeamId
                if (resolvedProjectId !== null) {
                    actions.setProjectTags(resolvedProjectId, tags)
                }
            },
            refreshTags: () => {
                const projectId = teamLogic.values.currentTeamId
                if (projectId === null) {
                    return
                }

                actions.setActiveProjectId(projectId)
                actions.setProjectTags(projectId, [])
                actions.loadTags()
            },
            [teamLogic.actionTypes.loadCurrentTeamSuccess]: ({ currentTeam }) => {
                actions.setActiveProjectId(currentTeam?.id ?? null)
            },
        }
    }),
])
